import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    ClockIcon,
    BellSimpleIcon,
    LightningIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AnimatedNumber, AnimatedDuration } from '@/components/AnimatedNumber';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import type { SystemOverview } from '@/types/system';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { OrganizationOverviewSkeleton } from './OrganizationOverviewSkeleton';

const CACHE_KEY = 'org-overview';

type StatValue =
    | { kind: 'number'; value: number }
    | { kind: 'duration'; totalSeconds: number };

interface StatItem {
    icon: Icon;
    label: string;
    value: StatValue;
    subtitle?:
        | { kind: 'count'; value: number; suffix: string }
        | { kind: 'text'; text: string };
}

function buildStats(
    data: SystemOverview,
    t: (key: string) => string,
    alertsEnabled: boolean,
): StatItem[] {
    const totalSources = data.sources.cameraCount + data.sources.fileCount;

    return [
        {
            icon: SecurityCameraIconMirrored,
            label: t('organization.sources'),
            value: { kind: 'number', value: totalSources },
            subtitle: {
                kind: 'text',
                text: `${data.sources.cameraCount} cameras, ${data.sources.fileCount} files`,
            },
        },
        {
            icon: ClockIcon,
            label: t('organization.searchableFootage'),
            value: { kind: 'duration', totalSeconds: data.totalDuration },
        },
        {
            icon: BellSimpleIcon,
            label: t('organization.events'),
            value: { kind: 'number', value: data.eventsCount },
            subtitle: {
                kind: 'count',
                value: data.eventsCount24H,
                suffix: ` ${t('organization.eventsToday')}`,
            },
        },
        ...(alertsEnabled
            ? [
                  {
                      icon: LightningIcon,
                      label: t('organization.alertRules'),
                      value: {
                          kind: 'number' as const,
                          value: data.activeAlertRulesCount ?? 0,
                      },
                  },
              ]
            : []),
    ];
}

export function OrganizationOverview() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const { alertsEnabled } = useFeatureFlags();
    const { data, isLoading, error } = useSWR<SystemOverview>(
        CACHE_KEY,
        () => AdminSettingsService.getOrgOverview(),
        { revalidateOnFocus: false },
    );

    if (isLoading) {
        return (
            <OrganizationOverviewSkeleton title={t('organization.overview')} />
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-col gap-4">
                <h3 className="text-sm font-medium text-white-100">
                    {t('organization.overview')}
                </h3>
                <div className="border border-danger-300 rounded-[5px] p-4 text-danger-200 text-sm">
                    {t('organization.overviewFailed')}
                </div>
            </div>
        );
    }

    const stats = buildStats(data, t, alertsEnabled);

    return (
        <div className="flex flex-col gap-4">
            <h3 className="text-sm font-medium text-white-100">
                {t('organization.overview')}
            </h3>
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
                {stats.map((stat) => (
                    <div
                        key={stat.label}
                        className="bg-transparent border border-grey-600 rounded-[10px] p-4 flex flex-col gap-3"
                    >
                        <div className="flex items-center gap-1">
                            <stat.icon
                                className="size-[14px] text-grey-200"
                                weight="regular"
                            />
                            <span className="text-sm text-grey-200">
                                {stat.label}
                            </span>
                        </div>
                        <div className="text-lg text-white leading-7">
                            {stat.value.kind === 'duration' ? (
                                <AnimatedDuration
                                    totalSeconds={stat.value.totalSeconds}
                                    willChange
                                />
                            ) : (
                                <AnimatedNumber
                                    value={stat.value.value}
                                    willChange
                                />
                            )}
                        </div>
                        {stat.subtitle && (
                            <div className="text-xs text-grey-300 inline-flex items-center gap-1">
                                {stat.subtitle.kind === 'count' ? (
                                    <>
                                        <AnimatedNumber
                                            value={stat.subtitle.value}
                                        />
                                        <span>{stat.subtitle.suffix}</span>
                                    </>
                                ) : (
                                    <span>{stat.subtitle.text}</span>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
